<template>
  <div class="section container">
    <div class="section-header">
      <div class="section-title">
        <div class="section-icon section-icon-password" :class="brandClass()"></div>
        <div class="section-title-label">
          {{$t("profile_account_data_profile_password_summary_title")}}
        </div>
      </div>
      <div @keydown="ariaClick" role="button" tabindex="0" @click="editClicked()" class="section-edit pointer" :class="{'hidden': editDisabled}" v-if="!editMode">
        <div class="section-edit-icon"></div>
        <div class="section-edit-label">
          {{$t("profile_edit_button")}}
        </div>
      </div>
    </div>
    <form v-if="editMode" novalidate class="form" @submit.prevent="validatePassword">
      <div v-show="false" class="custom-field">
        <OneInput
          :brandClass="brandClass()"
          v-model.trim="form.email" 
          :form="v$.form"     
          :label="''" 
          :paramName="'email'"
          :id="'email'"
          :autocomplete="'username'"
          :type="'text'"
          :showToggle="true"
          :errors="[]"
        />
      </div>
      <div class="custom-field">
        <OneInput
          :brandClass="brandClass()"
          v-model.trim="form.oldpassword" 
          :form="v$.form"     
          :label="$t('generic_old_password_label')+'*'" 
          :paramName="'oldpassword'"
          :id="'oldpassword'"
          :profileStyle="true"
          :autocomplete="'current-password'"
          :type="showOldPassword ? 'text' : 'password'"
          @blur="touchPassword('oldpassword')"
          v-on:keyup="passwordReplace('oldpassword', true);"
          v-on:keydown="passwordReplace('oldpassword')"
          :showToggle="true"
          @toggleClick="()=>showOldPassword = !showOldPassword"
          :errors="[
            {message:$t('error_old_password_required'), thereIsTheError:v$.form.oldpassword.required.$invalid},
          ]"
        />
      </div>
      <div class="custom-field">
        <OneInput
          :brandClass="brandClass()"
          v-model.trim="form.password" 
          :form="v$.form"     
          :label="$t('profile_account_data_profile_password_edit_label')+'*'" 
          :paramName="'password'"
          :autocomplete="'new-password'"
          :id="'password'"
          :profileStyle="true"
          :type="showPassword ? 'text' : 'password'"
          passwordrules="minlength: 10; maxlength: 40; required: lower; required: upper; required: digit; required: [-&quot;#&\'()*+,./:;@_~]"
          @blur="touchPassword('password')"
          v-on:keyup="passwordReplace('password', true);"
          v-on:keydown="passwordReplace('password')"
          :showToggle="true"
          @toggleClick="()=>showPassword = !showPassword"
          :errors="[
            {message:$t('error_password_required'), thereIsTheError:v$.form.password.required.$invalid},
            {message:$t('error_password_not_allowed'), thereIsTheError:passwordNotAllowed},
            {message:$t('error_password_part_of_email'), thereIsTheError:passwordNotEmailPart},
          ]"
        />
      </div>
      <OnePasswordStrength
        v-if="form.password && form.password.trim() && !passwordNotEmailPart && !passwordNotAllowed"
        :brandClass="brandClass()"
        :t="$t"
        :showNewPolicy="$c('show_new_password_policy')"
        class="password_strength"
        :strength="strength"
        :passwordRules="passwordRules"
      ></OnePasswordStrength>

      <div class="mandatory" style="order:100">*{{$t("generic_mandatory_fields")}}</div>
      <div class="buttonsContainer flex_button_container">
        <OneButton
              :brandClass="brandClass()"
          :profileStyle="true"
          :type="'primary'"
          :label="$t('profile_save_button')"
          :disabled="isButtonDisabled"
          @click="validatePassword()"
        />
        <OneButton
              :brandClass="brandClass()"
          :profileStyle="true"
          :type="'secondary'"
          :label="$t('profile_cancel_button')"
          @click="cancelClicked()"
        />
      </div>
    </form>
    <div v-if="!editMode">
      <div class="infos password-information">
        <div class="info">
          <div class="value password-value">************</div>
        </div>
      </div>
    </div>
    <hr>
  </div>
</template>
<style lang="scss" scoped>
  .password_strength{
    margin-top: 0px;
  }

  .section-icon-password {
    background-image: url('~@/assets/default/light/password-default-light.svg');
    &.__d{
      background-image: url('~@/assets/default/dark/password-default-dark.svg');
    }
    &.__alphabet{
      background-image: url('~@/assets/alphabet/light/password-alphabet-light.svg');
    }
    &.__m {
      background-image: url('~@/assets/mini/light/password-mini-light.svg');
      &.__d{
        background-image: url('~@/assets/mini/dark/password-mini-dark.svg') !important;
      }
    }
    &.__rr {
      background-image: url('~@/assets/default/dark/password-default-dark.svg');
    }
  }
</style>
<script>
import useVuelidate from '@vuelidate/core'
import oneUtils from "../../../../gcdm-one-library/gcdm-one-utils/src/bootstrap";

import {
  required
} from "@vuelidate/validators";
import { defineAsyncComponent } from 'vue'



export default {
  inject:['$c', '$t', 'brandClass'],
  setup() {
    return { v$: useVuelidate() }
  },
  name: "ProfilePassword",
  //validations from vuelidate
  mixins: [oneUtils.accessibilityMixin,oneUtils.passwordRulesMixin,oneUtils.customValidationMixin],
  components:{
    OnePasswordStrength: defineAsyncComponent(() =>
      import('../../../../gcdm-one-library/gcdm-one-password-strength/src/OnePasswordStrength')
    )
  },
  props: {
    user: {},
    editDisabled: {
      default: false,
      type: Boolean,
    },
  },
  data: () => ({
    form: {
      password: null,
      oldpassword:null,
      email: null
    },
    editMode: false,
  }),
  //vuelidate custom validations
  validations: {
    form: {
      password: {
        required
      },
      oldpassword: {
        required
      },
      email:{}
    },
  },
  computed: {
    isButtonDisabled: function () {
      return (
        this.v$.$invalid || !this.isPasswordFollowingRules || this.passwordNotEmailPart ||
        this.passwordNotAllowed
      );
    },
  },
  mounted: function() {
    this.form.email = this.user.email;
    this.touchForm("email");
  },
  methods: {
    /**
     * Tracy tracking: change pw intention
     * call to edit management function
     */
    editClicked(){
      this.handleEditMode();
    },
    /**
     * Tracy tracking: change pw cancelled
     * call to edit management function
     */
    cancelClicked(){
      this.handleEditMode();
    },
    /**
     * call to submit update function
     * call to edit management function
     */
    validatePassword() {
      this.submitUpdate();
      this.handleEditMode();
    },
    /**
     * change password API 
     * errors management
     */
    submitUpdate() {
      oneUtils.utils.showGlobalLoader();
      oneUtils.httpClient
      .changePassword(this.form.oldpassword, this.form.password, window['$startupwebcomponent'] && window['$startupwebcomponent'].oneid_access_token)
        .then(() => {
          oneUtils.utils.showMessage("SUCCESS", this.$t("profile_user_success"));
        })
        .catch((err) => {
          if (err.errorCode==oneUtils.enums.errorReasons.PASSWORD_HISTORY){
            oneUtils.utils.showMessage("ERROR", this.$t("generic_error_password_history_personal"));
          }else if(err.errorCode==oneUtils.enums.errorReasons.WRONG_PASSWORD){
            oneUtils.utils.showMessage("ERROR", this.$t("generic_error_profile_wrong_password"));
          }else{
            oneUtils.utils.showMessage("WARNING", this.$t("generic_system_error_personal"));
          }
        })
        .finally(()=>{
          oneUtils.utils.hideGlobalLoader();
        });
    },
    /**
     * Change editMode value
     */
    handleEditMode(){
      this.editMode = !this.editMode;
    }
  },
};
</script>
